/**
 * Which transactions of the selected paidol have a split draft (SEE-1828).
 *
 * The transactions table reads this to show the draft indicator without a lookup per
 * row; the side sheet keeps it current as drafts are saved and removed. Loaded once per
 * paidol (and again after a row click reopens a draft the list didn't know about).
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from 'app/store/rootReducer';
import { listSplitDraftIds } from 'app/services/split.service';

export interface SplitDraftsState {
  paidolId: string | null;
  ids: Record<string, true>;
  loaded: boolean;
}

export const initialState: SplitDraftsState = { paidolId: null, ids: {}, loaded: false };

export const fetchSplitDraftIds = createAsyncThunk<{ paidolId: string; ids: string[] }, { paidolId: string }>(
  'splitDrafts/fetch',
  async ({ paidolId }) => ({ paidolId, ids: await listSplitDraftIds(paidolId) })
);

const splitDraftsSlice = createSlice({
  name: 'splitDrafts',
  initialState,
  reducers: {
    splitDraftSaved(state, action: PayloadAction<{ transactionId: string }>) {
      state.ids[action.payload.transactionId] = true;
    },
    splitDraftRemoved(state, action: PayloadAction<{ transactionId: string }>) {
      delete state.ids[action.payload.transactionId];
    },
    /** Paidol-scoped: cleared on company switch and sign-out (SEE-1320 / SEE-1574). */
    resetSplitDraftsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSplitDraftIds.fulfilled, (state, action) => {
      state.paidolId = action.payload.paidolId;
      state.ids = Object.fromEntries(action.payload.ids.map((id) => [id, true]));
      state.loaded = true;
    });
  },
});

export const { splitDraftSaved, splitDraftRemoved, resetSplitDraftsSlice } = splitDraftsSlice.actions;

export const selectSplitDraftIds = (state: RootState): Record<string, true> | undefined =>
  state.splitDrafts?.ids;

export const selectHasSplitDraft = (state: RootState, transactionId: string | undefined): boolean =>
  !!transactionId && !!selectSplitDraftIds(state)?.[transactionId];

export default splitDraftsSlice.reducer;
